import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./CspDetailsStyles.scss";
import { InfoService } from "../../../services/site/InfoService";
// Import SCSS: import './sds_security_styles.scss';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

// Animated section wrapper
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: any;
}> = ({ children, className = "", variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_security-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_security-hero-title">
                Website Security Protection
              </h1>
              <h2 className="subtitle is-4 sds_security-hero-subtitle mb-5">
                Shield Your Website from Cyber Threats - Zero Downtime
              </h2>
              <div className="content is-size-5 sds_security-hero-description">
                <p className="mb-4">
                  <strong>Is Your Website Safe from Hackers?</strong> Every day,
                  thousands of websites get hacked. Customer data stolen,
                  business reputation ruined, and money lost. Don't let this
                  happen to you!
                </p>
                <p className="mb-4">
                  Our Content Security Policy (CSP) implementation protects your
                  website from cyber attacks while keeping it running smoothly -
                  your customers won't even notice we're working!
                </p>
              </div>
              <div className="sds_security-feature-badges">
                <span className="tag is-large sds_security-badge">
                  🛡️ Zero Downtime
                </span>
                <span className="tag is-large sds_security-badge">
                  ⚡ No Speed Impact
                </span>
                <span className="tag is-large sds_security-badge">
                  🔒 24/7 Protection
                </span>
                <span className="tag is-large sds_security-badge">
                  ✅ Compliant
                </span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_security-hero-visual"
            >
              <div className="sds_security-animation">
                <motion.div
                  className="sds_security-shield-icon"
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  🛡️
                </motion.div>
                <div className="sds_security-threat-icons">
                  {["💉", "🎣", "🦠", "🔓"].map((emoji, i) => (
                    <motion.div
                      key={i}
                      className="sds_security-threat-icon"
                      animate={{
                        scale: [1, 0.5, 1],
                        opacity: [0.7, 0.3, 0.7],
                      }}
                      transition={{
                        duration: 2 + i * 0.3,
                        repeat: Infinity,
                        delay: i * 0.3,
                      }}
                    >
                      {emoji}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          className="sds_security-highlight-banner"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          🔒 Protect Your Website in 24 Hours - ZERO Downtime Guaranteed!
        </motion.div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              What is Content Security Policy?
            </p>
            <p className="mt-2">
              Learn how CSP protects your website from cyber threats
            </p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Security Score Comparison Section
const SecurityScoreSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_security-score section sds_security-bg-success">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            📊 Watch Your Security Score Skyrocket!
          </h2>
        </div>

        <div className="columns is-vcentered">
          <motion.div
            className="column"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="sds_security-score-box sds_security-score-before-box">
              <p className="sds_security-score-label">BEFORE Protection</p>
              <motion.p
                className="sds_security-score-value sds_security-score-before"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                35/100
              </motion.p>
              <span className="sds_security-score-badge sds_security-badge-danger">
                ❌ VULNERABLE
              </span>
            </div>
          </motion.div>

          <div className="column is-narrow">
            <motion.div
              className="sds_security-arrow-up"
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ⬆️
            </motion.div>
          </div>

          <motion.div
            className="column"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="sds_security-score-box sds_security-score-after-box">
              <p className="sds_security-score-label">AFTER Protection</p>
              <motion.p
                className="sds_security-score-value sds_security-score-after"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                95/100
              </motion.p>
              <span className="sds_security-score-badge sds_security-badge-success">
                ✅ HIGHLY SECURE
              </span>
            </div>
          </motion.div>
        </div>

        <div className="notification is-light mt-5">
          <p className="has-text-centered is-size-4 has-text-weight-bold">
            🎯 Average Security Score Improvement:{" "}
            <span className="has-text-success">60-70 Points!</span>
          </p>
        </div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              Security Score Improvement
            </p>
            <p className="mt-2">Real-time demonstration of score changes</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Zero Downtime Implementation Section
const ZeroDowntimeSection: React.FC = () => {
  const steps = [
    {
      icon: "🔍",
      title: "Analysis",
      desc: "We study your website without touching anything",
    },
    {
      icon: "🧪",
      title: "Test Mode",
      desc: "Security policies tested in background",
    },
    {
      icon: "📈",
      title: "Gradual Rollout",
      desc: "Slowly activate protections layer by layer",
    },
    {
      icon: "✅",
      title: "Full Protection",
      desc: "Complete security without any interruption",
    },
  ];

  return (
    <AnimatedSection className="sds_security-downtime section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            ⚡ Zero Downtime Implementation
          </h2>
          <p className="subtitle is-5">
            Worried about your website going offline? Relax! We work behind the
            scenes.
          </p>
        </div>

        <motion.div
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <motion.div
                className="sds_security-step-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_security-step-number">{index + 1}</div>
                <div className="sds_security-step-icon">{step.icon}</div>
                <h4 className="title is-5 has-text-centered mb-3">
                  {step.title}
                </h4>
                <p className="has-text-centered">{step.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="sds_security-highlight-banner mt-5"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          💯 Your Website Stays Online 100% - Customers Experience ZERO
          Disruption!
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Cyber Threats Section
const CyberThreatsSection: React.FC = () => {
  const threats = [
    {
      icon: "💉",
      title: "Code Injection Attacks",
      desc: "Hackers trying to inject malicious code into your website to steal data or take control",
    },
    {
      icon: "🎣",
      title: "Data Theft Attempts",
      desc: "Criminals trying to steal your customer's credit cards, passwords, and personal information",
    },
    {
      icon: "🦠",
      title: "Malware & Viruses",
      desc: "Harmful software trying to infect your website and spread to your visitors' computers",
    },
    {
      icon: "👤",
      title: "Fake Content Injection",
      desc: "Unauthorized content or ads being injected into your pages without your knowledge",
    },
    {
      icon: "🎭",
      title: "Session Hijacking",
      desc: "Attackers stealing customer login sessions to access their accounts and data",
    },
    {
      icon: "🔓",
      title: "Unauthorized Access",
      desc: "Hackers attempting to access restricted areas of your website and admin panels",
    },
  ];

  return (
    <AnimatedSection className="sds_security-threats section sds_security-bg-danger">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            🚨 Cyber Threats We Block Automatically
          </h2>
          <p className="subtitle is-5">
            These are real dangers that can destroy your business. We stop them
            all!
          </p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {threats.map((threat, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_security-threat-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_security-threat-icon-large">
                  {threat.icon}
                </div>
                <h4 className="sds_security-threat-title">{threat.title}</h4>
                <p className="has-text-centered has-text-grey-dark mb-4">
                  {threat.desc}
                </p>
                <span className="sds_security-blocked-badge">
                  ✅ 100% BLOCKED
                </span>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <div className="notification is-light mt-5">
          <p className="has-text-centered is-size-4 has-text-weight-bold">
            🎯 24/7 Automatic Protection - Even While You Sleep!
          </p>
        </div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              Threat Protection Demo
            </p>
            <p className="mt-2">See how we block real cyber attacks</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Performance Benefits Section
const PerformanceBenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: "⚡",
      title: "Lightning Fast Performance",
      desc: "Our security works at browser level - adds ZERO milliseconds to your page load time.",
      points: [
        "No server overhead",
        "No extra bandwidth usage",
        "No database queries added",
        "Customer experience unchanged",
      ],
    },
    {
      icon: "📱",
      title: "Mobile Optimized",
      desc: "Security protection works perfectly on all devices without any performance issues.",
      points: [
        "Works on all smartphones",
        "Tablet compatible",
        "Desktop protection",
        "All browsers supported",
      ],
    },
    {
      icon: "💰",
      title: "No Extra Costs",
      desc: "No need to upgrade servers or buy expensive hardware. Works with your existing setup!",
      points: [
        "No server upgrades needed",
        "No bandwidth increase",
        "No hardware costs",
        "One-time implementation",
      ],
    },
  ];

  return (
    <AnimatedSection className="sds_security-performance section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            🚀 Maximum Security, Zero Speed Impact!
          </h2>
          <p className="subtitle is-5">
            Many security solutions slow down your website. Not us!
          </p>
        </div>

        <motion.div
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div
                className="sds_security-benefit-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_security-benefit-icon">{benefit.icon}</div>
                <h4 className="title is-4 has-text-centered mb-3">
                  {benefit.title}
                </h4>
                <p className="has-text-centered has-text-grey mb-4">
                  {benefit.desc}
                </p>
                <ul className="sds_security-benefits-list">
                  {benefit.points.map((point, i) => (
                    <li key={i}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              Performance Benchmark
            </p>
            <p className="mt-2">
              Speed tests before and after CSP implementation
            </p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Compliance Standards Section
const ComplianceSection: React.FC = () => {
  const standards = [
    {
      icon: "💳",
      title: "PCI DSS",
      desc: "Payment Card Industry Data Security Standard",
    },
    {
      icon: "🏥",
      title: "HIPAA",
      desc: "Health Insurance Portability and Accountability Act",
    },
    { icon: "🌍", title: "GDPR", desc: "General Data Protection Regulation" },
    {
      icon: "📋",
      title: "ISO 27001",
      desc: "International security management standard",
    },
    {
      icon: "🛡️",
      title: "SOC 2",
      desc: "Service Organization Control for cloud security",
    },
    {
      icon: "🏛️",
      title: "CERT-In",
      desc: "Indian government cybersecurity guidelines",
    },
  ];

  return (
    <AnimatedSection className="sds_security-compliance section sds_security-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            ✅ Meet Industry Compliance Standards
          </h2>
          <p className="subtitle is-5">Stop worrying about compliance audits</p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {standards.map((standard, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_security-compliance-card"
                whileHover={{ scale: 1.05 }}
              >
                <div className="sds_security-compliance-icon">
                  {standard.icon}
                </div>
                <h4 className="title is-5 has-text-centered mb-2">
                  {standard.title}
                </h4>
                <p className="has-text-centered has-text-grey">
                  {standard.desc}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Comparison Table Section
const ComparisonSection: React.FC = () => {
  const comparisons = [
    { feature: "Implementation Time", diy: "2-4 Weeks", hdt: "24-48 Hours" },
    { feature: "Website Downtime", diy: "Hours to Days", hdt: "ZERO" },
    { feature: "Performance Impact", diy: "Slow Loading", hdt: "No Impact" },
    { feature: "Ongoing Monitoring", diy: "Manual", hdt: "24/7 Automatic" },
    { feature: "Security Updates", diy: "You Handle It", hdt: "Automatic" },
    { feature: "Expert Support", diy: "None", hdt: "24/7 Available" },
    {
      feature: "Cost",
      diy: "High (Time + Risk)",
      hdt: "Affordable & Risk-Free",
    },
  ];

  return (
    <AnimatedSection className="sds_security-comparison section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            💡 Why Choose HDT Security Protection?
          </h2>
        </div>

        <div className="box">
          <div className="table-container">
            <table className="table is-fullwidth is-hoverable sds_security-comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="has-text-centered">❌ DIY Security</th>
                  <th className="has-text-centered">✅ HDT Protection</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    viewport={{ once: true }}
                  >
                    <td className="has-text-weight-bold">{row.feature}</td>
                    <td className="has-text-centered has-text-grey">
                      {row.diy}
                    </td>
                    <td className="has-text-centered has-text-success has-text-weight-bold">
                      {row.hdt}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              DIY vs Professional Comparison
            </p>
            <p className="mt-2">Real-world scenarios and outcomes</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Success Stories Section
const SuccessStoriesSection: React.FC = () => {
  const stories = [
    {
      icon: "🛒",
      title: "E-commerce Store",
      desc: "Security score jumped from 32 to 94 in 48 hours! Customer trust increased immediately.",
      metric: "+62 Points",
      color: "#4CAF50",
    },
    {
      icon: "🏦",
      title: "Banking Portal",
      desc: "Blocked 1,247 attack attempts in first month. Zero downtime during implementation!",
      metric: "1,247 Blocked",
      color: "#FF9800",
    },
    {
      icon: "🏥",
      title: "Healthcare System",
      desc: "HIPAA compliance achieved in 3 days. Patient data 100% secure now!",
      metric: "100% Compliant",
      color: "#9C27B0",
    },
  ];

  return (
    <AnimatedSection className="sds_security-stories section sds_security-bg-success">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            🎉 Real Results from Real Businesses
          </h2>
        </div>

        <motion.div
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stories.map((story, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <motion.div
                className="sds_security-story-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div
                  className="sds_security-story-icon"
                  style={{
                    background: `linear-gradient(135deg, ${story.color}, ${story.color}dd)`,
                  }}
                >
                  {story.icon}
                </div>
                <h4 className="title is-5 has-text-centered mb-3">
                  {story.title}
                </h4>
                <p className="has-text-centered has-text-grey mb-4">
                  {story.desc}
                </p>
                <p
                  className="sds_security-story-metric"
                  style={{ color: story.color }}
                >
                  {story.metric}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// FAQ Section
const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: "Will CSP implementation break my website?",
      answer:
        "No! We use a gradual, zero-downtime approach. We test everything in report-only mode first, then slowly activate protections. Your website stays online 100%.",
    },
    {
      question: "How long does implementation take?",
      answer:
        "Most websites are fully protected within 24-48 hours. Complex websites may take up to 5 days, but there's still zero downtime throughout the process.",
    },
    {
      question: "Will it slow down my website?",
      answer:
        "Absolutely not! CSP works at the browser level and adds zero milliseconds to your page load time. In fact, by blocking malicious scripts, your site may even load faster!",
    },
    {
      question: "What if something goes wrong?",
      answer:
        "We offer 24/7 emergency support. Plus, we can instantly roll back any changes if needed. We also provide 3 months of free monitoring to ensure everything works perfectly.",
    },
    {
      question: "Do I need technical knowledge?",
      answer:
        "Not at all! We handle everything from start to finish. You just need to provide access to your website, and we take care of the rest.",
    },
    {
      question: "Is this a one-time payment or subscription?",
      answer:
        "We offer both options. One-time implementation with optional ongoing monitoring, or a monthly subscription that includes updates and 24/7 support.",
    },
  ];

  return (
    <AnimatedSection className="sds_security-faq section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_security-section-title">
            ❓ Frequently Asked Questions
          </h2>
        </div>

        <div className="columns is-centered">
          <div className="column is-10">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="box sds_security-faq-box mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="title is-5 mb-3">
                  <span className="sds_security-faq-number">{index + 1}.</span>{" "}
                  {faq.question}
                </h4>
                <p className="has-text-grey-dark">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="sds_security-video-container mt-6">
          <motion.div
            className="sds_security-video-placeholder"
            whileHover={{ scale: 1.02 }}
          >
            <span className="icon is-large">
              <i className="fas fa-play-circle fa-4x"></i>
            </span>
            <p className="has-text-weight-bold mt-4 is-size-4">
              Common Questions Answered
            </p>
            <p className="mt-2">Detailed FAQ video walkthrough</p>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_security-cta section">
      <div className="container">
        <div className="box sds_security-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">
                Don't Wait Until You Get Hacked!
              </h2>
              <p className="subtitle is-4 mb-6">
                Every minute your website is unprotected, you're at risk.
                Hackers don't sleep.
              </p>
{/* 
              <div className="sds_security-offer-box mb-5">
                <p className="is-size-3 has-text-weight-bold mb-4 has-text-danger">
                  🚨 LIMITED TIME OFFER
                </p>
                <div className="content is-size-5">
                  <p className="mb-2">✅ FREE Security Audit (Worth ₹25,000)</p>
                  <p className="mb-2">✅ Zero Downtime Implementation</p>
                  <p className="mb-2">✅ 3 Months Free Monitoring</p>
                  <p className="mb-2">✅ 24/7 Emergency Support</p>
                  <p className="has-text-danger has-text-weight-semibold mt-4">
                    ⏰ Protect your website today - Tomorrow might be too late!
                  </p>
                </div>
              </div> */}

              <div className="buttons is-centered are-large">
                <motion.a
                  href="tel:+91 9553896536"
                  className="button is-primary is-large sds_security-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                {/* <motion.a
                  href="mailto:sds@hdtsolution.com"
                  className="button is-danger is-large sds_security-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Audit</span>
                </motion.a> */}
              </div>

              <div className="sds_security-video-container mt-6">
                <motion.div
                  className="sds_security-video-placeholder"
                  whileHover={{ scale: 1.02 }}
                >
                  <span className="icon is-large">
                    <i className="fas fa-play-circle fa-4x"></i>
                  </span>
                  <p className="has-text-weight-bold mt-4 is-size-4">
                    Getting Started Guide
                  </p>
                  <p className="mt-2">
                    Quick security implementation walkthrough
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Footer Section
const FooterSection: React.FC = () => {
  return (
    <footer className="sds_security-footer section">
      <div className="container">
        <div className="columns">
          <div className="column is-4">
            <h4 className="title is-5 mb-4">HDT Solutions</h4>
            <p className="mb-4">
              Protecting businesses from cyber threats since 2018. Your security
              is our priority.
            </p>
            <div className="sds_security-social-links">
              <a
                href="#"
                className="sds_security-social-icon"
                aria-label="Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href="#"
                className="sds_security-social-icon"
                aria-label="Twitter"
              >
                <i className="fab fa-twitter"></i>
              </a>
              <a
                href="#"
                className="sds_security-social-icon"
                aria-label="LinkedIn"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a
                href="#"
                className="sds_security-social-icon"
                aria-label="Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
            </div>
          </div>

          <div className="column is-4">
            <h4 className="title is-5 mb-4">Quick Links</h4>
            <ul className="sds_security-footer-links">
              <li>
                <a href="#hero">Home</a>
              </li>
              <li>
                <a href="#threats">Security Threats</a>
              </li>
              <li>
                <a href="#benefits">Benefits</a>
              </li>
              <li>
                <a href="#compliance">Compliance</a>
              </li>
              <li>
                <a href="#faq">FAQ</a>
              </li>
              <li>
                <a href="#contact">Contact</a>
              </li>
            </ul>
          </div>

          <div className="column is-4">
            <h4 className="title is-5 mb-4">Contact Us</h4>
            <div className="content">
              <p className="mb-3">
                <span className="icon has-text-primary">
                  <i className="fas fa-phone"></i>
                </span>
                <a href="tel:+91 9553896536">{InfoService.mobile_no_1}</a>
              </p>
              <p className="mb-3">
                <span className="icon has-text-primary">
                  <i className="fas fa-envelope"></i>
                </span>
                <a href="mailto:sds@hdtsolution.com">sds@hdtsolution.com</a>
              </p>
              <p className="mb-3">
                <span className="icon has-text-primary">
                  <i className="fas fa-globe"></i>
                </span>
                <a
                  href="https://hdtsecure.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  hdtsolution.com
                </a>
              </p>
              <p>
                <span className="icon has-text-primary">
                  <i className="fas fa-map-marker-alt"></i>
                </span>
                Hyderabad, India
              </p>
            </div>
          </div>
        </div>

        <hr className="sds_security-footer-divider" />

        <div className="has-text-centered">
          <p className="sds_security-footer-copyright">
            © {new Date().getFullYear()} HDT Solutions (Hyper Digi
            Technologies). All rights reserved.
          </p>
          <p className="sds_security-footer-legal">
            <a href="/privacy-policy">Privacy Policy</a> |
            <a href="/terms-conditions">Terms & Conditions</a> |
            <a href="/cookie-policy">Cookie Policy</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Component
const CspDetails: React.FC = () => {
  return (
    <div className="sds_security-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: "none" }}>
        <h1>HDT Solutions - Content Security Policy Implementation</h1>
        <meta
          name="description"
          content="Protect your website from cyber threats with zero downtime. Professional CSP implementation by HDT Solutions. 24/7 security monitoring and compliance."
        />
        <meta
          name="keywords"
          content="content security policy, website security, cyber security, CSP implementation, zero downtime, website protection"
        />
      </div>

      <HeroSection />
      <SecurityScoreSection />
      <ZeroDowntimeSection />
      <CyberThreatsSection />
      <PerformanceBenefitsSection />
      <ComplianceSection />
      <ComparisonSection />
      <SuccessStoriesSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};

export default CspDetails;
